import { Aperture, Bot, BrainCircuit, FileText, Image, Mic, Video } from 'lucide-react';
import type { Flow } from './types.ts';

export const categories = [
  'All Categories',
  'Text Generation',
  'Image Processing',
  'Video Analysis',
  'Audio Transcription',
  'Chatbots',
  'Data Extraction'
];

export const mockFlows: Flow[] = [
  {
    id: 1,
    name: 'Summarize-It',
    description: 'A powerful flow to summarize long texts into concise paragraphs.',
    author: 'AI-Builders',
    authorAvatar: '/avatars/01.png',
    tags: ['text', 'summarization', 'nlp'],
    rating: 4.8,
    downloads: 12500,
    credits: 5,
    featured: true,
    image: '/flow-images/summarize.png',
    icon: FileText,
    category: 'Text Generation',
    version: '1.2.0',
    lastUpdated: '2025-08-15',
    difficulty: 'Beginner',
    inputs: [
      { name: 'text', type: 'string', required: true, description: 'The text to summarize.' },
      { name: 'max_length', type: 'number', required: false, default: 150, description: 'Maximum length of the summary.' }
    ],
    outputs: [{ name: 'summary', type: 'string', description: 'The generated summary.' }],
    documentation:
      'This flow uses a large language model to generate a summary of the input text. You can control the approximate length of the summary with the max_length parameter.',
    comments: [
      { id: 1, user: 'UserA', avatar: '/avatars/02.png', rating: 5, comment: 'Works perfectly!', timestamp: '2 weeks ago', likes: 15 },
      {
        id: 2,
        user: 'UserB',
        avatar: '/avatars/03.png',
        rating: 4,
        comment: 'Good, but sometimes a bit too short.',
        timestamp: '1 month ago',
        likes: 4
      },
      {
        id: 3,
        user: 'UserC',
        avatar: '/avatars/04.png',
        rating: 3,
        comment: 'Average experience, could be improved.',
        timestamp: '2 months ago',
        likes: 2
      }
    ]
  },
  {
    id: 2,
    name: 'Image Enhancer',
    description: 'Automatically enhance the quality of your images using AI.',
    author: 'VisionCraft',
    authorAvatar: '/avatars/02.png',
    tags: ['image', 'enhancement', 'computer-vision'],
    rating: 4.9,
    downloads: 25000,
    credits: 10,
    featured: true,
    image: '/flow-images/enhancer.png',
    icon: Image,
    category: 'Image Processing',
    version: '2.0.1',
    lastUpdated: '2025-08-20',
    difficulty: 'Intermediate',
    inputs: [{ name: 'image', type: 'image', required: true, description: 'The image to enhance.' }],
    outputs: [{ name: 'enhanced_image', type: 'image', description: 'The enhanced image.' }],
    documentation:
      'This flow applies a series of AI-powered filters to improve image quality, including upscaling, noise reduction, and color correction.',
    comments: []
  },
  {
    id: 3,
    name: 'Video Captioner',
    description: 'Generate captions for your videos in multiple languages.',
    author: 'Media-AI',
    authorAvatar: '/avatars/03.png',
    tags: ['video', 'captioning', 'transcription'],
    rating: 4.7,
    downloads: 8000,
    credits: 15,
    featured: false,
    image: '/flow-images/captioner.png',
    icon: Video,
    category: 'Video Analysis',
    version: '1.0.0',
    lastUpdated: '2025-07-30',
    difficulty: 'Advanced',
    inputs: [
      { name: 'video', type: 'video', required: true, description: 'The video file to caption.' },
      { name: 'language', type: 'string', required: false, default: 'en', description: 'The language for the captions (e.g., en, es, fr).' }
    ],
    outputs: [{ name: 'captions', type: 'string', description: 'The generated captions in SRT format.' }],
    documentation: 'Processes a video file, transcribes the audio, and returns captions in the specified language.',
    comments: []
  },
  {
    id: 4,
    name: 'Customer Support Bot',
    description: 'A friendly chatbot to answer customer questions.',
    author: 'Chatterbox',
    authorAvatar: '/avatars/04.png',
    tags: ['chatbot', 'customer-support', 'nlp'],
    rating: 4.9,
    downloads: 50000,
    credits: 0,
    featured: true,
    image: '/flow-images/chatbot.png',
    icon: Bot,
    category: 'Chatbots',
    version: '3.1.0',
    lastUpdated: '2025-08-21',
    difficulty: 'Intermediate',
    inputs: [
      { name: 'user_query', type: 'string', required: true, description: "The customer's question." },
      { name: 'knowledge_base', type: 'text', required: false, description: "Optional knowledge base to supplement the bot's responses." }
    ],
    outputs: [{ name: 'response', type: 'string', description: "The bot's answer." }],
    documentation: 'A pre-trained customer support chatbot that can be customized with your own knowledge base.',
    comments: []
  }
];

export const mockWorkflowTemplates = {
  1: [
    {
      id: 101,
      name: 'Blog Post Assistant',
      description: 'Uses the Summarize-It flow to help draft blog posts from long-form content.',
      author: 'ContentCreators',
      thumbnail: '/workflow-images/blog-assistant.png',
      rating: 4.9,
      downloads: 500
    }
  ],
  2: [
    {
      id: 102,
      name: 'Social Media Image Prep',
      description:
        'A workflow that takes raw images, enhances them with Image Enhancer, and resizes them for different social media platforms.',
      author: 'MarketingPros',
      thumbnail: '/workflow-images/social-media-prep.png',
      rating: 4.8,
      downloads: 1200
    }
  ]
};
